import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resolve photo URL: full URLs as-is, relative paths prefixed with backend
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND}${url}`;
};

export default function ManagePhotos() {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/photos`);
      setPhotos(data);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Image is too large (max 10 MB)');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Pick a photo first');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = caption
        ? `${API}/photos/upload?caption=${encodeURIComponent(caption)}`
        : `${API}/photos/upload`;
      await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded');
      setCaption('');
      setFile(null);
      setPreview('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#D4AF37]" />
          Upload Photo from Phone or Computer
        </h3>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label className="text-white mb-2">Choose Photo</Label>
              <Input
                ref={fileInputRef}
                data-testid="photo-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="bg-[#0A0F1C] border-white/20 text-white file:text-[#D4AF37] file:bg-[#1E2A42] file:border-0 file:rounded file:px-3 file:py-1.5 file:mr-3 file:font-bold cursor-pointer"
              />
              <p className="text-[#9BA3AF] text-xs mt-1">JPG, PNG, GIF or WEBP (max 10 MB)</p>
            </div>
            <div>
              <Label className="text-white mb-2">Caption (optional)</Label>
              <Input
                data-testid="photo-caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="bg-[#0A0F1C] border-white/20 text-white"
                placeholder="Goal celebration!"
              />
            </div>
          </div>

          {preview && (
            <div className="flex items-center gap-4">
              <img src={preview} alt="preview" className="w-32 h-32 object-cover rounded-lg border border-white/10" />
              <div>
                <p className="text-white text-sm font-semibold">{file?.name}</p>
                <p className="text-[#9BA3AF] text-xs">{Math.round((file?.size || 0) / 1024)} KB</p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            data-testid="upload-photo-button"
            disabled={uploading || !file}
            className="bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
          >
            {uploading ? 'Uploading...' : 'Upload Photo'}
          </Button>
        </form>
      </Card>

      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
          All Photos ({photos.length})
        </h3>
        {photos.length === 0 ? (
          <p className="text-[#9BA3AF]">No photos uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.id}
                data-testid={`admin-photo-${photo.id}`}
                className="relative group rounded-lg overflow-hidden aspect-square"
              >
                <img src={resolveUrl(photo.url)} alt={photo.caption || 'Photo'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  {photo.caption && (
                    <p className="text-white text-sm text-center">{photo.caption}</p>
                  )}
                  <Button
                    data-testid={`delete-photo-${photo.id}`}
                    onClick={() => handleDelete(photo.id)}
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
